import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import { supabase } from '../lib/supabase';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<'loading' | 'form' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password strength check (same as SignUp page)
  const isPasswordStrong = password.length >= 8;
  const passwordStrengthText = password.length > 0 
    ? (isPasswordStrong ? 'Excellent password!' : `${8 - password.length} more characters needed`)
    : '';

  useEffect(() => {
    const handlePasswordReset = async () => {
      try {
        // Get all URL parameters
        const accessToken = searchParams.get('access_token');
        const refreshToken = searchParams.get('refresh_token');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        console.log('=== Password Reset Debug ===');
        console.log('URL:', window.location.href);
        console.log('All search params:', Object.fromEntries(searchParams.entries()));
        console.log('Reset params:', { 
          accessToken: accessToken ? 'present' : 'missing',
          refreshToken: refreshToken ? 'present' : 'missing',
          error,
          errorDescription
        });

        // Check for error parameters first
        if (error) {
          console.error('Password reset error from Supabase:', error, errorDescription);
          setStatus('error');
          setErrorMessage(errorDescription || 'Password reset failed. Please try again.');
          return;
        }

        // Set session if tokens are provided
        if (accessToken && refreshToken) {
          console.log('Setting session for password reset');
          
          const { data, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (sessionError) {
            console.error('Session error:', sessionError);
            setStatus('error');
            setErrorMessage('Failed to complete password reset. Please try again.');
            return;
          }

          console.log('Session set successfully for password reset');
          setStatus('form');
          return;
        }

        // If no tokens, check if user is already authenticated
        const { data: { user }, error: userError } = await supabase.auth.getUser();

        if (userError) {
          console.error('User check error:', userError);
          setStatus('error');
          setErrorMessage('Invalid or expired reset link. Please request a new one.');
          return;
        }

        if (user) {
          console.log('User is authenticated, showing password reset form');
          setStatus('form');
          return;
        }

        // If we reach here, no valid session found
        console.log('No valid session found for password reset');
        setStatus('error');
        setErrorMessage('Invalid or expired reset link. Please request a new one.');

      } catch (err) {
        console.error('Unexpected error during password reset:', err);
        setStatus('error');
        setErrorMessage('An unexpected error occurred. Please try again.');
      }
    };

    handlePasswordReset();
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset errors
    setErrorMessage('');

    // Basic validation
    if (!password.trim()) {
      setErrorMessage('Password is required');
      return;
    }

    if (password.length < 8) {
      setErrorMessage('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        console.error('Password update error:', error);
        setErrorMessage(error.message || 'Failed to update password. Please try again.');
        return;
      }

      setStatus('success');
    } catch (err) {
      console.error('Unexpected error during password update:', err);
      setErrorMessage('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusContent = () => {
    switch (status) {
      case 'loading':
        return {
          icon: (
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="32" cy="32" r="32" fill="#177508"/>
              <path d="M32 8C18.745 8 8 18.745 8 32s10.745 24 24 24 24-10.745 24-24S45.255 8 32 8zm0 44c-11.046 0-20-8.954-20-20s8.954-20 20-20 20 8.954 20 20-8.954 20-20 20z" fill="white"/>
              <path d="M32 16c-8.837 0-16 7.163-16 16s7.163 16 16 16 16-7.163 16-16-7.163-16-16-16zm0 28c-6.627 0-12-5.373-12-12s5.373-12 12-12 12 5.373 12 12-5.373 12-12 12z" fill="white"/>
            </svg>
          ),
          title: 'Setting up password reset...',
          message: 'Please wait while we prepare your password reset.',
          color: '#177508'
        };
      
      case 'success':
        return {
          icon: (
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="32" cy="32" r="32" fill="#177508"/>
              <path d="M20 32L28 40L44 24" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ),
          title: 'Password Updated Successfully!',
          message: 'Your password has been updated. You can now sign in with your new password.',
          color: '#177508'
        };
      
      case 'error':
        return {
          icon: (
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="32" cy="32" r="32" fill="#C9121A"/>
              <path d="M20 20L44 44M44 20L20 44" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ),
          title: 'Reset Failed',
          message: errorMessage,
          color: '#C9121A'
        };
      
      default:
        return {
          icon: (
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="32" cy="32" r="32" fill="#177508"/>
              <path d="M32 8C18.745 8 8 18.745 8 32s10.745 24 24 24 24-10.745 24-24S45.255 8 32 8zm0 44c-11.046 0-20-8.954-20-20s8.954-20 20-20 20 8.954 20 20-8.954 20-20 20z" fill="white"/>
              <path d="M32 16c-8.837 0-16 7.163-16 16s7.163 16 16 16 16-7.163 16-16-7.163-16-16-16zm0 28c-6.627 0-12-5.373-12-12s5.373-12 12-12 12 5.373 12 12-5.373 12-12 12z" fill="white"/>
            </svg>
          ),
          title: 'Set New Password',
          message: 'Enter your new password below.',
          color: '#177508'
        };
    }
  };

  const content = getStatusContent();

  if (status === 'success') {
    return (
      <div className='verification-container'>
        <div className='verification-content'>
          <div className='verification-card'>
            <div className='verification-icon'>
              {content.icon}
            </div>
            
            <h1 className='titles large color black'>{content.title}</h1>
            
            <p className='paragraphs medium color charcoal'>
              {content.message}
            </p>
            
            <div className='verification-actions'>
              <button 
                className='btn primary'
                onClick={() => navigate('/')}
              >
                Return to Home
              </button>
            </div>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className='verification-container'>
        <div className='verification-content'>
          <div className='verification-card'>
            <div className='verification-icon'>
              {content.icon}
            </div>
            
            <h1 className='titles large color black'>{content.title}</h1>
            
            <p className='paragraphs medium color charcoal'>
              {content.message}
            </p>
            
            <div className='verification-actions'>
              <button 
                className='btn primary'
                onClick={() => navigate('/auth/forgot-password')}
              >
                Request New Reset
              </button>
              <button 
                className='btn secondary'
                onClick={() => navigate('/')}
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className='verification-container'>
      <div className='verification-content'>
        <div className='verification-card'>
          <div className='verification-icon'>
            {content.icon}
          </div>
          
          <h1 className='titles large color black'>{content.title}</h1>
          
          <p className='paragraphs medium color charcoal'>
            {content.message}
          </p>
          
          {status === 'form' && (
            <form onSubmit={handleSubmit}>
              <div className='form-group'>
                <label>
                  <span className='labels large color black'>New password <span className='required'>*</span></span>
                  <div className='password-input-container'>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      placeholder='Your new password'
                      className='paragraphs medium color black'
                      disabled={isSubmitting}
                    />
                    <button
                      type="button"
                      className='password-toggle'
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </button>
                  </div>
                  {password.length > 0 && (
                    <div className={`password-strength ${isPasswordStrong ? 'strong' : 'weak'}`}>
                      <div className='strength-indicator'>
                        <div className={`strength-bar ${isPasswordStrong ? 'filled' : ''}`}></div>
                      </div>
                      <span className='strength-text'>{passwordStrengthText}</span>
                    </div>
                  )}
                </label>
              </div>
              
              <div className='form-group'>
                <label>
                  <span className='labels large color black'>Confirm new password <span className='required'>*</span></span>
                  <div className='password-input-container'>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      placeholder='Confirm your new password'
                      className='paragraphs medium color black'
                      disabled={isSubmitting}
                    />
                    <button
                      type="button"
                      className='password-toggle'
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </button>
                  </div>
                  {confirmPassword.length > 0 && (
                    <div className={`password-strength ${password === confirmPassword ? 'strong' : 'weak'}`}>
                      <span className='strength-text'>
                        {password === confirmPassword ? 'Passwords match' : 'Passwords do not match'}
                      </span>
                    </div>
                  )}
                </label>
              </div>
              
              {errorMessage && (
                <div className="error-message general-error">
                  {errorMessage}
                </div>
              )}
              
              <div className='verification-actions'>
                <button 
                  type="submit"
                  className='btn primary'
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          )}
          
          {status === 'loading' && (
            <div className='loading-spinner'>
              <div className='spinner'></div>
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default ResetPassword; 